import * as Immutable from 'immutable';
import * as t from 'io-ts';
import queryString from 'query-string';
import { Action, createActions } from 'redux-actions';
import { xml2js } from 'xml-js';

import { ENDPOINTS_AUTH, ENDPOINTS_USER, REACT_APP_GOOGLE_ADS_ID_CRISPIN } from '../../config/';
import { localeData } from '../../locales/data';
import { AccountMetadataValidation, BaseCreateAccountData, UserAccountCreate } from '../../reducers/AccountReducer/AccountReducer.types';
import { reduceObjectMap } from '../../reducers/AccountReducer/Helpers';
import {
  defaultErrorMessage,
  globalFetch,
  ioTsValidationError,
  isAction,
  webSimApiErrorFlashMessages,
} from '../../shared/helpers/fetch-helper';
import { logErrorService } from '../../shared/services';
import analytics from '../../shared/services/analytics/analytics';
import {
  checkIfAuth,
  checkIfSessionExpired,
  errorMessageLogin,
  getUserInformation,
  saveUserData,
  sessionExpire,
  userLogout,
} from '../AuthenticationActions';
import { addFlashMessages } from '../FlashMessagesActions';
import { invalidRequest } from '../GenericActions';
import { captchaExecuteAsync, CAPTCHA_RETRY_SYMBOL } from '../../reducers/CaptchaReducer/CaptchaActions';
import { CaptchaReturnType } from '../../reducers/CaptchaReducer/Captcha.type';

import { checkIfServerIsDown, createAccountfetch, createAccountfetchErrors, setEditErrorMsg } from './Helper';
import { trackSignUpField } from './trackSignUpField';
import { SIGN_UP, SUPPORT_URL_PLACEHOLDER } from '../../shared/constants';
import moment from 'moment';
import { AppDispatch, GetState, RootState } from '../../shared/services/store';
import { encodeToBase64 } from '../../shared/helpers/encodeToBase64';
import { localStorageService } from '../../shared/services/localStorageService';
import { PathReporter } from 'io-ts/lib/PathReporter';
import { getUserCompetitions } from '../CompetitionsActions';
import { getUserIdSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';

export const {
  responseUserOptions,
  accountChangeMetadata,
  accountChangeTerms,
  accountTermsAccepted,
  accountValidToSubmit,
  accountChangeFieldConnectSaved,
  setFormState,
  changeFieldValuesAccount,
  requestAccount,
  responseAccount,
  responseAccountFailure,
  responseAccountFail,
  requestUserOptions,
  responseUserOptionsFailure,
  requestAccountVerifyEmail,
  responseAccountVerifyEmail,
  responseAccountVerifyEmailFailure,
  cleanAccount,
  disableAccountFinished,
  editAccountData,
  accountChangeEmail,
  accountGenerateAccessToken,
  changeUserEmail,
  cleanChangeEmail,
  accountVerifyEmail500,
  setReferrer,
  toggleAccountDetailsAgree,
  toggleIsAgreedAlphathonUpdateAccountDetails,
  undoEditUserData,
  setPhotoDirty,
  setResumeDirty,
  updateRecertificationProgress,
  cleanRecertificationProgress,
  requestToken,
  responseTokenFail,
  cleanToken,
  cleanWorkday,
  cleanBrainLabs,
  updateConsultantRequiredFields,
} = createActions(
  {
    RESPONSE_USER_OPTIONS: (data: any) => ({ data }),
    ACCOUNT_CHANGE_METADATA: (data: any) => ({ data }),
    ACCOUNT_GENERATE_ACCESS_TOKEN: (data: any) => ({ data }),
    ACCOUNT_CHANGE_TERMS: (field: string, data: any) => ({ field, data }),
    ACCOUNT_TERMS_ACCEPTED: (data: boolean) => ({ data }),
    ACCOUNT_VALID_TO_SUBMIT: (data: boolean) => ({ data }),
    ACCOUNT_CHANGE_FIELD_CONNECT_SAVED: (data: any, currentFieldSaved: any = null, onblur: boolean = false) => ({
      data,
      currentFieldSaved,
      onblur,
    }),
    SET_FORM_STATE: (data: any) => ({ data }),
    UNDO_EDIT_USER_DATA: (data: any, isConsultant: boolean) => ({ isConsultant, data }),
  },
  'REQUEST_ACCOUNT',
  'RESPONSE_ACCOUNT',
  'RESPONSE_ACCOUNT_FAILURE',
  'RESPONSE_ACCOUNT_FAIL',
  'REQUEST_USER_OPTIONS',
  'RESPONSE_USER_OPTIONS_FAILURE',
  'REQUEST_ACCOUNT_VERIFY_EMAIL',
  'RESPONSE_ACCOUNT_VERIFY_EMAIL',
  'RESPONSE_ACCOUNT_VERIFY_EMAIL_FAILURE',
  'CLEAN_ACCOUNT',
  'DISABLE_ACCOUNT_FINISHED',
  'EDIT_ACCOUNT_DATA',
  'ACCOUNT_CHANGE_EMAIL',
  'CLEAN_CHANGE_EMAIL',
  'ACCOUNT_VERIFY_EMAIL500',
  'SET_REFERRER',
  'TOGGLE_ACCOUNT_DETAILS_AGREE',
  'TOGGLE_IS_AGREED_ALPHATHON_UPDATE_ACCOUNT_DETAILS',

  'SET_PHOTO_DIRTY',
  'SET_RESUME_DIRTY',
  'UPDATE_RECERTIFICATION_PROGRESS',
  'CLEAN_RECERTIFICATION_PROGRESS',
  'REQUEST_TOKEN',
  'RESPONSE_TOKEN_FAIL',
  'CLEAN_TOKEN',
  'CLEAN_WORKDAY',
  'CLEAN_BRAIN_LABS',
  'UPDATE_CONSULTANT_REQUIRED_FIELDS'
);

export const accountChangeFieldConnect =
  (fieldDataOrEvent: any) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<any> => {
    const state = getState();
    const userFieldData = state.get('user').toJS();
    const name = fieldDataOrEvent?.currentTarget?.name || fieldDataOrEvent.name;
    const currentFieldSaved = !userFieldData.data ? null : reduceObjectMap(userFieldData.data)[name];

    dispatch(trackField(fieldDataOrEvent));

    if (fieldDataOrEvent.type === 'blur') {
      return;
    }

    // @ts-ignore
    if (state.getIn(['user', 'permissions'])?.includes('CONSULTANT')) {
      fieldDataOrEvent.isConsultant = true;
    }
    return dispatch(accountChangeFieldConnectSaved(fieldDataOrEvent, currentFieldSaved));
  };

const trackField =
  (fieldDataOrEvent: any) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<any> => {
    const isSignUpPage = window.location.pathname.includes(SIGN_UP);
    if (isSignUpPage) {
      dispatch(trackSignUpField(fieldDataOrEvent));
    }
  };

export const undoEditUserDataWithPermission =
  (data: any) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<any> => {
    // @ts-ignore
    const isConsultant = getState()?.getIn(['user', 'permissions'])?.includes('CONSULTANT');
    dispatch(undoEditUserData(data, isConsultant));
  };

export const fetchUserOptions =
  () =>
  async (dispatch: AppDispatch): Promise<object> => {
    dispatch(requestUserOptions());

    return globalFetch(
      ENDPOINTS_USER.user,
      {
        method: 'OPTIONS',
        credentials: 'include',
        headers: new Headers({ 'Content-Type': 'application/json' }),
      },
      dispatch,
      () => dispatch(responseUserOptionsFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (json.type === 'RESPONSE_USER_OPTIONS_FAILURE') {
          return json;
        } else if (t.exact(AccountMetadataValidation).decode(json).isRight()) {
          const data = {
            country: json.actions.POST.address.children.country.choices,
            defaultCountry: json.actions.POST.address.children.country.default,
            degree: json.actions.POST.education.children.degree.choices,
            university: json.actions.POST.education.children.university.choices,
            gender: json.actions.POST.gender.choices,
            graduationYear: {
              maxValue: json.actions.POST.education.children.graduationYear.maxValue,
              minValue: json.actions.POST.education.children.graduationYear.minValue,
            },
          };

          return dispatch(responseUserOptions(Immutable.fromJS(data)));
        } else {
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AccountMetadataValidation);

          return dispatch(responseUserOptionsFailure());
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(responseUserOptionsFailure());
      });
  };

export const signUpStorageKey = 'signUpEventDate';
export function getIsRecentSignUp() {
  const lastSentEventTime = localStorageService?.getItem(signUpStorageKey);
  if (lastSentEventTime) {
    const lastDate = moment(lastSentEventTime as string);
    const throttlePeriodInHours = 24;
    const hoursSinceLastSignUp = moment().diff(lastDate, 'hours');

    if (hoursSinceLastSignUp < throttlePeriodInHours) {
      return true;
    }
  }
  return false;
}

export function trackSignUpEvent() {
  try {
    if (getIsRecentSignUp()) {
      logErrorService(new Error('repeating sign up'));
    } else {
      analytics.trackCustomEvent('sign_up_ab');
    }

    analytics.trackPredefinedEvent('SignUp');
    // google ads event for crispin
    // https://jira.worldquant.com/browse/BRAINTECH-2816
    if (REACT_APP_GOOGLE_ADS_ID_CRISPIN) {
      analytics.trackCustomEvent('conversion', {
        send_to: 'AW-357055808/69bKCI29l6AZEMD6oKoB',
        value: 1.0,
        currency: 'USD',
      });
    }
    localStorageService?.setItem(signUpStorageKey, moment().format());
  } catch (err) {
    logErrorService(err, err);
  }
}

export const fetchCreateAccount =
  (data: BaseCreateAccountData) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object> => {
    const state = getState();

    if (state.getIn(['account', 'isFetching']) === true) {
      return dispatch(invalidRequest());
    }

    const decoded = t.exact(UserAccountCreate).decode(data);
    if (decoded.isRight()) {
      dispatch(requestAccount());

      return createAccountfetch(data)
        .then((response: any) => {
          if (response.status === 201) {
            return response
              .json()
              .then(async (json: any): Promise<object> => {
                trackSignUpEvent();

                const approved = 'approved' in json && json.approved;
                const locked = 'detail' in json && json.detail === 'LOCKED';

                if (approved) {
                  // @ts-ignore todo: turn into real action or stop dispatching it
                  return dispatch(checkIfSessionExpired(false)).then((action: Action) => {
                    if (action.type === 'AUTHENTICATION_LOGIN') {
                      return dispatch(checkIfAuth()).then(() => {
                        dispatch(responseAccount({ locked, approved }));
                        dispatch(saveUserData(json, state.getIn(['competitions', 'competition'])));
                      });
                    } else {
                      return dispatch(responseAccount({ locked, approved }));
                    }
                  });
                } else {
                  return dispatch(responseAccount({ locked, approved }));
                }
              })
              .catch(() => {
                webSimApiErrorFlashMessages(dispatch);

                return dispatch(responseAccountFailure());
              });
          } else if (response.status === 405) {
            dispatch(
              addFlashMessages([
                {
                  msg: 'api.error.brain.experiencing',
                  defaultMessage: localeData.en['api.error.brain.experiencing'],
                },
              ])
            );

            return dispatch(responseAccountFailure());
          } else if (response.status === 409) {
            dispatch(
              addFlashMessages(
                [{ msg: 'api.error.brain.signUpConflict', defaultMessage: localeData.en['api.error.brain.signUpConflict'] }],
                {
                  links: [
                    {
                      urls: ['/forgot-password', SUPPORT_URL_PLACEHOLDER],
                      keys: ['api.error.brain.signUpConflict.link'],
                      defaultMsgkeys: [localeData.en['api.error.brain.signUpConflict.link']],
                    },
                  ],
                }
              )
            );

            return dispatch(responseAccountFailure());
          } else {
            return response
              .json()
              .then(async (json: any): Promise<object> => createAccountfetchErrors(json, dispatch, responseAccountFailure, true))
              .catch(() => {
                logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);
                webSimApiErrorFlashMessages(dispatch);

                return dispatch(responseAccountFailure());
              });
          }
        })
        .catch(() => {
          logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);
          webSimApiErrorFlashMessages(dispatch);

          return dispatch(responseAccountFailure());
        });
    } else {
      console.error(PathReporter.report(decoded));

      logErrorService(PathReporter.report(decoded));
      webSimApiErrorFlashMessages(dispatch);

      return dispatch(responseAccountFailure());
    }
  };

export function accountVerifyEmail(token: string | undefined) {
  return async (dispatch: AppDispatch, getState: GetState): Promise<object> => {
    if (token === undefined) {
      return dispatch(invalidRequest());
    }
    const state = getState();

    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL as unknown as object;
    }

    dispatch(requestAccountVerifyEmail());

    return fetch(ENDPOINTS_USER.verify_email, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ captcha }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.status === 200) {
          return response.json().then((json: any) => {
            if (json && json.detail === 'EMAIL_CHANGED' && state.getIn(['user', 'isLoggedIn']) === true) {
              // @ts-ignore todo: turn into real action or stop dispatching it
              void dispatch(getUserInformation('self', false));
            }

            return dispatch(responseAccountVerifyEmail(json));
          });
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          return dispatch(responseAccountVerifyEmailFailure());
        } else if (response.status >= 500 && response.status <= 504) {
          dispatch(accountVerifyEmail500());

          return checkIfServerIsDown(dispatch, response, () => dispatch(responseAccountVerifyEmailFailure()));
        }

        return response.json().then(async (json: any): Promise<object> => {
          logErrorService(localeData.en['api.error.brain.experiencing'], json);
          webSimApiErrorFlashMessages(dispatch);

          return dispatch(responseAccountVerifyEmailFailure());
        });
      })
      .catch(() => dispatch(responseAccountVerifyEmailFailure()));
  };
}

export const changeEmailUser =
  () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object> => {
    const state = getState();

    if (state.getIn(['account', 'isfetching']) === true) {
      return dispatch(invalidRequest());
    }

    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL as unknown as object;
    }

    dispatch(requestAccount());
    const email = state.getIn(['user', 'data', 'email']);

    const changeEmail = state.getIn(['account', 'dataFields', 'changeEmail', 'value']);
    const password = state.getIn(['account', 'dataFields', 'changeEmailPassword', 'value']);

    const dataInformation = `${email}:${password}`;
    const headerAuth = `Basic ${encodeToBase64(dataInformation)}`;

    const callbackStatus400 = (response: any) => {
      response
        .json()
        .then((json: any) => {
          if (json && json.email) {
            dispatch(addFlashMessages([{ msg: json.email[0], defaultMessage: json.email[0] }]));
          }
        })
        .catch();
    };

    return globalFetch(
      ENDPOINTS_USER.change_email,
      {
        method: 'POST',
        credentials: 'include',
        headers: new Headers({
          Authorization: headerAuth,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ email: changeEmail, captcha }),
      },
      dispatch,
      () => dispatch(responseAccountFail()),
      {
        answerEmpty: true,
        retryEmpty: false,
        callbackStatus401: () =>
          dispatch(
            addFlashMessages([
              {
                msg: 'api.password.invalid',
                defaultMessage: localeData.en['api.password.invalid'],
              },
            ])
          ),
        callbackStatus400,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ACCOUNT_FAIL')) {
          return json;
        }
        // @ts-ignore todo: turn into real action or stop dispatching it
        dispatch(getUserInformation('self', false));

        return dispatch(accountChangeEmail(changeEmail));
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseAccountFail());
      });
  };

export const deactivateAccount =
  () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object> => {
    const state = getState();
    if (state.getIn(['account', 'isfetching']) === true) {
      return Promise.resolve({});
    }
    dispatch(requestAccount());

    const headers = new Headers();

    const fetchUrl = getEditFetchUrl();

    return fetch(fetchUrl, {
      method: 'DELETE',
      credentials: 'include',
      headers,
    })
      .then(async (response: any): Promise<object> => {
        if (response.status === 204) {
          // @ts-ignore fix signature mismatch
          void dispatch(userLogout());

          return dispatch(disableAccountFinished());
        } else if (response.status === 400) {
          return response
            .json()
            .then(async (json: any): Promise<object> => {
              if (json && json.errors) {
                const errors = json.errors.map((error: any) => ({
                  msg: error,
                  defaultMessage: error,
                }));
                dispatch(addFlashMessages(errors));

                return dispatch(responseAccountFailure());
              } else {
                return createAccountfetchErrors(json, dispatch, responseAccountFailure, true);
              }
            })
            .catch((err: any) => {
              logErrorService(err, err);

              return dispatch(responseAccountFailure());
            });
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);
          //@ts-ignore todo: turn into real action or stop dispatching it
          //tslint:disable-next-line:await-promise
          await dispatch(sessionExpire());

          return dispatch(responseAccountFailure());
        } else if (response.status >= 500 && response.status <= 504) {
          return checkIfServerIsDown(dispatch, response, () => dispatch(responseAccountFailure()));
        } else {
          return response
            .json()
            .then((json: any) => {
              logErrorService(json, json);
              if (Object.keys(json).length === 0) {
                webSimApiErrorFlashMessages(dispatch);
              } else {
                const errors = setEditErrorMsg(json);
                if (errors.length !== 0) {
                  dispatch(addFlashMessages(errors));
                }
              }

              return dispatch(responseAccountFailure());
            })
            .catch((err: any) => {
              logErrorService(err, err);
              webSimApiErrorFlashMessages(dispatch);

              return dispatch(responseAccountFailure());
            });
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseAccountFailure());
      });
  };

type Saml = {
  'saml2p:Response': { _attributes: { Destination: string } };
};

function getXmlDestination(xml: string) {
  const parsedXml = xml2js(xml, { compact: true });
  const saml = parsedXml as Saml;

  return saml['saml2p:Response']._attributes.Destination;
}

function openWorkday(xml: string, redirectUrl: string = '') {
  const form = document.createElement('form');
  const samlResponse = document.createElement('input');
  samlResponse.type = 'hidden';
  samlResponse.name = 'SAMLResponse';
  samlResponse.value = btoa(xml);
  form.appendChild(samlResponse);

  const relayState = document.createElement('input');
  relayState.type = 'hidden';
  relayState.name = 'RelayState';
  relayState.value = redirectUrl;
  form.appendChild(relayState);

  form.method = 'POST';
  form.action = getXmlDestination(xml);
  form.target = '_blank';

  document.body.append(form);
  form.submit();
  const removeFormHandler = () => form.remove();
  const formRemovalDelay = 5000;
  window.setTimeout(removeFormHandler, formRemovalDelay);
}

function openBrainLabs(xml: string) {
  const form = document.createElement('form');
  const samlResponse = document.createElement('input');
  samlResponse.type = 'hidden';
  samlResponse.name = 'SAMLResponse';
  const encodedXml = btoa(xml);
  samlResponse.value = encodedXml;
  form.appendChild(samlResponse);
  console.log(encodedXml);

  const parser = new DOMParser();
  const parsedXml = parser.parseFromString(xml, 'application/xml');
  const mainNode = parsedXml.childNodes[0];
  // @ts-ignore
  const relay = mainNode.attributes.Relay;

  const relayState = document.createElement('input');
  relayState.type = 'hidden';
  relayState.name = 'RelayState';
  relayState.value = relay.nodeValue;
  form.appendChild(relayState);

  form.method = 'POST';
  form.action = getXmlDestination(xml);
  form.target = '_blank';

  document.body.append(form);
  form.submit();
  const removeFormHandler = () => form.remove();
  const formRemovalDelay = 5000;
  window.setTimeout(removeFormHandler, formRemovalDelay);
}

function getRedirectUrl(state: RootState) {
  const stateRedirectUrl = state.getIn(['user', 'redirectUrl']);
  if (stateRedirectUrl) {
    return stateRedirectUrl;
  } else if (window.location.search.includes('return_to')) {
    const parsedQuery = queryString.parse(window.location.search);

    return parsedQuery.return_to;
  }

  return '';
}

export const loginToWorkday =
  () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const state = getState();

    if (state.getIn(['account', 'fetchingToken']) === true) {
      return dispatch(invalidRequest());
    }

    dispatch(requestToken());

    const email = state.getIn(['user', 'data', 'email']);

    const password = state.getIn(['account', 'dataFields', 'workdayPassword', 'value']);

    const dataInformation = `${email}:${password}`;
    const headerAuth = `Basic ${encodeToBase64(dataInformation)}`;

    let captcha: CaptchaReturnType;
    try {
      captcha = await dispatch(captchaExecuteAsync());
    } catch (err) {
      dispatch(responseTokenFail());
      throw err;
    }

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      dispatch(responseTokenFail());
      return CAPTCHA_RETRY_SYMBOL;
    }

    const callbackStatus400 = (response: any) => {
      response
        .json()
        .then((json: any) => {
          if (json && json.email) {
            dispatch(addFlashMessages([{ msg: json.email[0], defaultMessage: json.email[0] }]));
          }
        })
        .catch();
    };

    return globalFetch(
      `${ENDPOINTS_AUTH.workday}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: new Headers({
          Authorization: headerAuth,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ captcha }),
      },
      dispatch,
      () => dispatch(responseTokenFail()),
      {
        answerEmpty: false,
        retryEmpty: true,
        callbackStatus401: () =>
          dispatch(
            addFlashMessages([
              {
                msg: 'api.password.invalid',
                defaultMessage: localeData.en['api.password.invalid'],
              },
            ])
          ),
        callbackStatus400,
        isResponseJson: false,
      }
    )
      .then(async (text: string | null): Promise<object> => {
        if (typeof text === 'string' && text.startsWith('<saml2p:Response')) {
          const redirectUrl = getRedirectUrl(state);
          // @ts-ignore
          openWorkday(text, redirectUrl);
        }

        return dispatch(cleanWorkday());
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);
        dispatch(cleanWorkday());

        return dispatch(responseTokenFail());
      });
  };

export type ParsedParameter = string | string[] | null | undefined;

export const loginToBrainLabs =
  () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const state = getState();

    if (state.getIn(['account', 'fetchingToken']) === true) {
      return dispatch(invalidRequest());
    }

    dispatch(requestToken());

    const email = state.getIn(['user', 'data', 'email']);

    const password = state.getIn(['account', 'dataFields', 'brainLabsPassword', 'value']);

    const dataInformation = `${email}:${password}`;
    const headerAuth = `Basic ${encodeToBase64(dataInformation)}`;

    let captcha: CaptchaReturnType;
    try {
      captcha = await dispatch(captchaExecuteAsync());
    } catch (err) {
      dispatch(responseTokenFail());
      throw err;
    }

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      dispatch(responseTokenFail());
      return CAPTCHA_RETRY_SYMBOL;
    }

    const callbackStatus400 = (response: any) => {
      response
        .json()
        .then((json: any) => {
          if (json && json.email) {
            dispatch(addFlashMessages([{ msg: json.email[0], defaultMessage: json.email[0] }]));
          }
        })
        .catch();
    };

    return globalFetch(
      `${ENDPOINTS_AUTH.brainLabs}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: new Headers({
          Authorization: headerAuth,
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ captcha }),
      },
      dispatch,
      () => dispatch(responseTokenFail()),
      {
        answerEmpty: false,
        retryEmpty: true,
        callbackStatus401: () =>
          dispatch(
            addFlashMessages([
              {
                msg: 'api.password.invalid',
                defaultMessage: localeData.en['api.password.invalid'],
              },
            ])
          ),
        callbackStatus400,
        isResponseJson: false,
      }
    )
      .then(async (text: string | null): Promise<object> => {
        if (typeof text === 'string' && text.startsWith('<saml2p:Response')) {
          const redirectUrl = getRedirectUrl(state);
          // @ts-ignore
          openBrainLabs(text, redirectUrl);
        }

        return dispatch(cleanBrainLabs());
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);
        dispatch(cleanBrainLabs());

        return dispatch(responseTokenFail());
      });
  };

export const generateAccessToken =
  () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object> => {
    const state = getState();

    if (state.getIn(['account', 'fetchingToken']) === true) {
      return dispatch(invalidRequest());
    }

    dispatch(requestToken());

    const email = state.getIn(['user', 'data', 'email']);

    const password = state.getIn(['account', 'dataFields', 'accessTokenPassword', 'value']);

    const dataInformation = `${email}:${password}`;
    const headerAuth = `Basic ${encodeToBase64(dataInformation)}`;

    const callbackStatus400 = (response: any) => {
      response
        .json()
        .then((json: any) => {
          if (json && json.email) {
            dispatch(addFlashMessages([{ msg: json.email[0], defaultMessage: json.email[0] }]));
          }
        })
        .catch();
    };

    return globalFetch(
      `${ENDPOINTS_USER.token}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: new Headers({
          Authorization: headerAuth,
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseTokenFail()),
      {
        answerEmpty: false,
        retryEmpty: true,
        callbackStatus401: () =>
          dispatch(
            addFlashMessages([
              {
                msg: 'api.password.invalid',
                defaultMessage: localeData.en['api.password.invalid'],
              },
            ])
          ),
        callbackStatus400,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ACCOUNT_FAIL')) {
          return json;
        }

        if (json) {
          return dispatch(accountGenerateAccessToken(json.token));
        } else {
          return dispatch(responseTokenFail());
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseTokenFail());
      });
  };

export const reverifyEmailUser =
  (changeEmail: string) =>
  async (dispatch: AppDispatch): Promise<object> => {
    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL as unknown as object;
    }

    return globalFetch(
      ENDPOINTS_USER.reverify_email,
      {
        method: 'POST',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ email: changeEmail, captcha }),
      },
      dispatch,
      () => dispatch(responseAccountFail()),
      {
        answerEmpty: true,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ACCOUNT_FAIL')) {
          return json;
        } else {
          return { type: 'REVERIFY_EMAIL_USER_SUCCESS' };
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseAccountFail());
      });
  };

function getEditFetchUrl() {
  return `${ENDPOINTS_USER.user}/self`;
}

interface EditAccountUserParams {
  data: any;
  photoFile: File | null;
  showFlashMessage: boolean;
  resumeFile: File | null;
}

// tslint:disable-next-line:max-func-body-length
export const editAccountUser =
  ({ data, photoFile, showFlashMessage = true, resumeFile }: EditAccountUserParams) =>
  // tslint:disable-next-line:max-func-body-length
  async (dispatch: AppDispatch, getState: GetState): Promise<object> => {
    const state = getState();
    if (state.getIn(['account', 'isfetching']) === true) {
      return Promise.resolve({});
    }

    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL as unknown as object;
    }

    dispatch(requestAccount());

    // Nest captcha inside `auxiliary` to match the create-user payload contract
    // (see src/components/Account/Create/helpers.ts where it's set as
    // auxiliary.captcha). Preserve any pre-existing auxiliary fields (e.g. `agree`).
    const dataWithCaptcha = {
      ...data,
      auxiliary: {
        ...(data?.auxiliary ?? {}),
        captcha,
      },
    };

    const headers = new Headers();
    let payload = null;
    if (photoFile || resumeFile) {
      payload = new FormData();
      payload.append('user', JSON.stringify(dataWithCaptcha));
      if (photoFile) {
        payload.append('image', photoFile);
      }
      if (resumeFile) {
        payload.append('resume', resumeFile);
      }
    } else {
      headers.append('Content-Type', 'application/json');
      payload = JSON.stringify(dataWithCaptcha);
    }

    const fetchUrl = getEditFetchUrl();

    return fetch(fetchUrl, {
      method: 'PATCH',
      credentials: 'include',
      headers,
      body: payload,
    })
      .then(async (response: any): Promise<object> => {
        if (response.status === 200) {
          return response
            .json()
            .then(async (json: any): Promise<object> => {
              if (json.detail === 'LOCKED' || json.approved === false) {
                // @ts-ignore todo: turn into real action or stop dispatching it
                return dispatch(sessionExpire(false)).then(async (): Promise<object> => {
                  const errorMsg: any = errorMessageLogin('LOCKED');
                  dispatch(addFlashMessages(errorMsg.message, errorMsg.options));

                  return dispatch(editAccountData());
                });
              } else {
                const userId = getUserIdSelector(state);

                dispatch(saveUserData(json));
                // @ts-ignore
                void dispatch(checkIfAuth());
                void dispatch(getUserCompetitions(userId));

                return dispatch(editAccountData());
              }
            })
            .catch((err: any) => {
              logErrorService(err, err);
              dispatch(responseAccountFailure());

              return dispatch(editAccountData());
            });
        } else if (response.status === 400) {
          return response
            .json()
            .then(async (json: any): Promise<object> => {
              if (json && json.errors) {
                const errors = json.errors.map((error: any) => ({
                  msg: error,
                  defaultMessage: error,
                }));

                dispatch(addFlashMessages(errors));

                return dispatch(responseAccountFailure());
              } else {
                return createAccountfetchErrors(json, dispatch, responseAccountFailure, showFlashMessage);
              }
            })
            .catch((err: any) => {
              logErrorService(err, err);

              return dispatch(responseAccountFailure());
            });
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);
          //@ts-ignore todo: turn into real action or stop dispatching it
          //tslint:disable-next-line:await-promise
          await dispatch(sessionExpire());

          return dispatch(responseAccountFailure());
        } else if (response.status === 500) {
          logErrorService(response.statusText, response.status);
          webSimApiErrorFlashMessages(dispatch);

          return dispatch(responseAccountFailure());
        } else if (response.status >= 500 && response.status <= 504) {
          return checkIfServerIsDown(dispatch, response, () => dispatch(responseAccountFailure()));
        } else {
          return response
            .json()
            .then((json: any) => {
              logErrorService(json, json);
              if (Object.keys(json).length === 0) {
                webSimApiErrorFlashMessages(dispatch);
              } else {
                const errors = setEditErrorMsg(json);
                if (errors.length !== 0) {
                  dispatch(addFlashMessages(errors));
                }
              }

              return dispatch(responseAccountFailure());
            })
            .catch((err: any) => {
              logErrorService(err, err);
              webSimApiErrorFlashMessages(dispatch);

              return dispatch(responseAccountFailure());
            });
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseAccountFailure());
      });
  };
